import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../../components/Layout';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Hash, Save, Eye } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'sonner';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const formatPreview = (c, seq) => {
  const n = String(seq ?? c.next_number ?? 1).padStart(c.number_length || 6, '0');
  const segments = [];
  if (c.use_company_code) segments.push('1000');
  if (c.prefix) segments.push(c.prefix);
  segments.push(n);
  return segments.join(c.separator || '') + (c.suffix || '');
};

const EmployeeIDConfig = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const writable = canWrite('employee');
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${backendUrl}/api/hcm/employees/id-config`, { withCredentials: true });
      setConfig(res.data);
    } catch (error) {
      console.error('Failed to load ID config:', error);
      toast.error('Failed to load Employee ID configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const update = (field, value) => setConfig((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        prefix: config.prefix || '',
        suffix: config.suffix || '',
        separator: config.separator || '',
        number_length: parseInt(config.number_length, 10) || 6,
        next_number: parseInt(config.next_number, 10) || 1,
        use_company_code: !!config.use_company_code,
      };
      const res = await axios.put(`${backendUrl}/api/hcm/employees/id-config`, payload, { withCredentials: true });
      setConfig(res.data);
      toast.success('Employee ID format saved');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !config) {
    return (
      <Layout>
        <div className="p-6 text-gray-500" data-testid="emp-id-config-loading">Loading configuration...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6 max-w-4xl" data-testid="employee-id-config-page">
        <div>
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm')}>HCM</span>
            <span>/</span>
            <span>Employee ID Format</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-pink-100 text-pink-700"><Hash className="h-5 w-5" /></div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Employee ID Format</h1>
              <p className="text-gray-600 mt-0.5 text-sm">Configure how new employee numbers are generated.</p>
            </div>
          </div>
        </div>

        {/* Live preview */}
        <Card className="p-5 bg-gradient-to-br from-pink-50 to-white border-pink-100">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-2">
            <Eye className="h-4 w-4" /> Live Preview
          </div>
          <div className="font-mono text-3xl font-bold text-gray-900 tracking-wide" data-testid="emp-id-preview">
            {formatPreview(config)}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Next few IDs:{' '}
            <span className="font-mono">
              {[0, 1, 2].map((i) => formatPreview(config, (parseInt(config.next_number, 10) || 1) + i)).join(', ')}
            </span>
          </p>
        </Card>

        <Card className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="prefix">Prefix</Label>
              <Input
                id="prefix"
                data-testid="emp-id-prefix"
                value={config.prefix || ''}
                maxLength={10}
                onChange={(e) => update('prefix', e.target.value)}
                placeholder="EMP"
              />
              <p className="text-xs text-gray-500 mt-1">Leading text, e.g. EMP.</p>
            </div>
            <div>
              <Label htmlFor="suffix">Suffix</Label>
              <Input
                id="suffix"
                data-testid="emp-id-suffix"
                value={config.suffix || ''}
                maxLength={10}
                onChange={(e) => update('suffix', e.target.value)}
                placeholder="(optional)"
              />
              <p className="text-xs text-gray-500 mt-1">Trailing text (optional).</p>
            </div>
            <div>
              <Label htmlFor="separator">Separator</Label>
              <Input
                id="separator"
                data-testid="emp-id-separator"
                value={config.separator || ''}
                maxLength={3}
                onChange={(e) => update('separator', e.target.value)}
                placeholder="(none)"
              />
              <p className="text-xs text-gray-500 mt-1">Between segments, e.g. '-'.</p>
            </div>
            <div>
              <Label htmlFor="number_length">Number Length</Label>
              <Input
                id="number_length"
                data-testid="emp-id-number-length"
                type="number"
                min={1}
                max={12}
                value={config.number_length}
                onChange={(e) => update('number_length', e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">Zero-padded width of the running number (1–12).</p>
            </div>
            <div>
              <Label htmlFor="next_number">Next Number</Label>
              <Input
                id="next_number"
                data-testid="emp-id-next-number"
                type="number"
                min={1}
                value={config.next_number}
                onChange={(e) => update('next_number', e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">Starting number (never reuses an existing ID).</p>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3">
              <div>
                <Label htmlFor="use_company_code" className="cursor-pointer">Include Company Code</Label>
                <p className="text-xs text-gray-500 mt-1">Prepend the employee's company code.</p>
              </div>
              <Switch
                id="use_company_code"
                data-testid="emp-id-use-company-code"
                checked={!!config.use_company_code}
                onCheckedChange={(v) => update('use_company_code', v)}
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 border-t pt-4">
            {config.updated_at && (
              <span className="text-xs text-gray-400 mr-auto">
                Last updated {new Date(config.updated_at).toLocaleString()} by {config.updated_by || 'system'}
              </span>
            )}
            {writable ? (
              <Button onClick={handleSave} disabled={saving} data-testid="emp-id-save-btn">
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Configuration'}
              </Button>
            ) : (
              <span className="text-sm text-gray-400">Read-only (no employee write permission)</span>
            )}
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default EmployeeIDConfig;
